import { apiClient } from '@/lib/api-client'
import type {
  BrokerCertificationStatus,
  Certification,
  Enrollment,
  ExamAttemptResult,
  TrainingExam,
  TrainingMaterial,
  TrainingPath,
  TrainingStep,
} from '@/types/training'

// ─── Participant API ───

export const trainingService = {
  // My courses
  getMyCourses: () => apiClient.get<Enrollment[]>('/training/my-courses/'),

  getMyCourse: (enrollmentId: string) =>
    apiClient.get<Enrollment>(`/training/my-courses/${enrollmentId}/`),

  // Step actions
  startStep: (enrollmentId: string, stepId: string) =>
    apiClient.post(`/training/my-courses/${enrollmentId}/steps/${stepId}/start/`),

  completeStep: (enrollmentId: string, stepId: string, completionData?: Record<string, unknown>) =>
    apiClient.post(`/training/my-courses/${enrollmentId}/steps/${stepId}/complete/`, {
      completion_data: completionData,
    }),

  // Exam
  getExamPreStartInfo: (enrollmentId: string, stepId: string) =>
    apiClient.get<ExamPreStartInfo>(
      `/training/my-courses/${enrollmentId}/steps/${stepId}/exam/pre-start/`
    ),

  startExam: (enrollmentId: string, stepId: string) =>
    apiClient.post(`/training/my-courses/${enrollmentId}/steps/${stepId}/exam/start/`),

  submitExam: (enrollmentId: string, stepId: string, attemptId: string, answers: Record<string, unknown>) =>
    apiClient.post(`/training/my-courses/${enrollmentId}/steps/${stepId}/exam/submit/`, {
      attempt_id: attemptId,
      answers,
    }),

  /** Cancel an open attempt — broker explicitly chose to start over.
   * Hard-deletes the attempt server-side so no counter slot is burned. */
  abandonExam: (enrollmentId: string, stepId: string) =>
    apiClient.post<{ abandoned: boolean }>(
      `/training/my-courses/${enrollmentId}/steps/${stepId}/exam/abandon/`,
    ),

  /** Persist partial answers on the currently-open attempt. Used to
   * keep the broker's progress alive across navigation / refresh. */
  saveExamAnswers: (
    enrollmentId: string,
    stepId: string,
    attemptId: string,
    answers: Record<string, unknown>,
  ) =>
    apiClient.post<{ saved: boolean; answers: Record<string, unknown> }>(
      `/training/my-courses/${enrollmentId}/steps/${stepId}/exam/save-answers/`,
      { attempt_id: attemptId, answers },
    ),

  getExamResults: (enrollmentId: string, stepId: string, attemptId: string) =>
    apiClient.get<ExamAttemptResult>(
      `/training/my-courses/${enrollmentId}/steps/${stepId}/exam/results/${attemptId}/`
    ),

  // Material confirmation
  confirmMaterial: (enrollmentId: string, stepId: string, materialId: string, timeSpent?: number) =>
    apiClient.post(
      `/training/my-courses/${enrollmentId}/steps/${stepId}/materials/${materialId}/confirm/`,
      { time_spent_seconds: timeSpent || 0 }
    ),

  // Certifications
  getMyCertifications: () => apiClient.get<Certification[]>('/training/my-certifications/'),

  // Material details (participant endpoint)
  getMaterial: (materialId: string) =>
    apiClient.get<TrainingMaterial>(`/training/materials/${materialId}/`),

  // Broker certification gate
  getCertificationStatus: () =>
    apiClient.get<BrokerCertificationStatus>('/broker/certification-status/'),

  // Diploma render (post-test)
  getDiploma: (certificationId: string) =>
    apiClient.get<DiplomaRender>(`/training/certifications/${certificationId}/diploma/`),
}

// ─── Types for new endpoints ───

export interface ExamPreStartSection {
  name: string
  question_count: number
  mandatory_count: number
}

export interface ExamPreStartInfo {
  exam_id: string
  title: string
  description: string
  total_questions: number
  passing_score: number
  mandatory_count: number
  max_attempts: number | null
  attempts_used: number
  attempts_remaining: number | null
  time_limit_minutes: number | null
  sections: ExamPreStartSection[]
  last_attempt: null | {
    id: string
    passed: boolean | null
    score: number | null
    completed_at: string
  }
  /** When set, the broker has an attempt in progress for this step.
   *  The frontend should let them resume instead of starting fresh. */
  open_attempt: null | {
    id: string
    attempt_number: number
    started_at: string | null
    saved_answers: Record<string, string | string[]>
  }
}

export interface DiplomaRender {
  title: string
  subtitle: string
  diploma_html: string | null
  badge_html: string | null
  social_share_text: string
  social_share_hashtags: string
  certificate_number: string
  issued_at: string
  expires_at: string | null
}

// ─── Admin API ───

export const trainingAdminService = {
  // Training paths
  getPaths: (params?: Record<string, string>) =>
    apiClient.get<TrainingPath[]>('/training/admin/paths/', { params }),

  getPath: (id: string) => apiClient.get<TrainingPath>(`/training/admin/paths/${id}/`),

  createPath: (data: Partial<TrainingPath>) =>
    apiClient.post<TrainingPath>('/training/admin/paths/', data),

  updatePath: (id: string, data: Partial<TrainingPath>) =>
    apiClient.patch<TrainingPath>(`/training/admin/paths/${id}/`, data),

  deletePath: (id: string) => apiClient.delete(`/training/admin/paths/${id}/`),

  duplicatePath: (id: string) =>
    apiClient.post<TrainingPath>(`/training/admin/paths/${id}/duplicate/`),

  reorderSteps: (id: string, stepIds: string[]) =>
    apiClient.post(`/training/admin/paths/${id}/reorder-steps/`, { step_ids: stepIds }),

  // Steps
  getSteps: (params?: Record<string, string>) =>
    apiClient.get<TrainingStep[]>('/training/admin/steps/', { params }),

  createStep: (data: Partial<TrainingStep>) =>
    apiClient.post<TrainingStep>('/training/admin/steps/', data),

  updateStep: (id: string, data: Partial<TrainingStep>) =>
    apiClient.patch<TrainingStep>(`/training/admin/steps/${id}/`, data),

  deleteStep: (id: string) => apiClient.delete(`/training/admin/steps/${id}/`),

  // Exams
  getExams: (params?: Record<string, string>) =>
    apiClient.get<TrainingExam[]>('/training/admin/exams/', { params }),

  createExam: (data: Partial<TrainingExam>) =>
    apiClient.post<TrainingExam>('/training/admin/exams/', data),

  updateExam: (id: string, data: Partial<TrainingExam>) =>
    apiClient.patch<TrainingExam>(`/training/admin/exams/${id}/`, data),

  deleteExam: (id: string) => apiClient.delete(`/training/admin/exams/${id}/`),

  previewExam: (id: string) =>
    apiClient.post(`/training/admin/exams/${id}/preview/`),

  // Materials
  getMaterials: (params?: Record<string, string>) =>
    apiClient.get<TrainingMaterial[]>('/training/admin/materials/', { params }),

  createMaterial: (data: Partial<TrainingMaterial>) =>
    apiClient.post<TrainingMaterial>('/training/admin/materials/', data),

  updateMaterial: (id: string, data: Partial<TrainingMaterial>) =>
    apiClient.patch<TrainingMaterial>(`/training/admin/materials/${id}/`, data),

  deleteMaterial: (id: string) => apiClient.delete(`/training/admin/materials/${id}/`),

  getUploadUrl: (filename: string, contentType: string) =>
    apiClient.post<{ upload_url: string; s3_key: string }>('/training/admin/materials/upload-url/', {
      filename,
      content_type: contentType,
    }),

  /**
   * Upload an interactive HTML artifact (Claude artifact, reveal.js deck,
   * Slidev export, etc.). The backend stores the HTML and returns a
   * TrainingMaterial with material_type='interactive_html' + content_url.
   */
  createArtifact: (payload: {
    title: string
    description?: string
    html_content: string
    company_id?: string
  }) =>
    apiClient.post<TrainingMaterial>('/training/admin/materials/artifact/', payload),

  // Enrollments
  getEnrollments: (params?: Record<string, string>) =>
    apiClient.get('/training/admin/enrollments/', { params }),

  getEnrollment: (id: string) => apiClient.get(`/training/admin/enrollments/${id}/`),

  enrollUser: (trainingPathId: string, userId: string) =>
    apiClient.post('/training/admin/enrollments/', {
      training_path_id: trainingPathId,
      user_id: userId,
    }),

  bulkEnroll: (trainingPathId: string, userIds: string[]) =>
    apiClient.post('/training/admin/enrollments/bulk/', {
      training_path_id: trainingPathId,
      user_ids: userIds,
    }),

  skipStep: (enrollmentId: string, stepId: string, reason: string) =>
    apiClient.post(`/training/admin/enrollments/${enrollmentId}/skip-step/`, {
      step_id: stepId,
      reason,
    }),

  approveStep: (enrollmentId: string, stepId: string, notes?: string) =>
    apiClient.post(`/training/admin/enrollments/${enrollmentId}/approve-step/`, {
      step_id: stepId,
      notes: notes || '',
    }),

  suspendEnrollment: (id: string, reason: string) =>
    apiClient.post(`/training/admin/enrollments/${id}/suspend/`, { reason }),

  reactivateEnrollment: (id: string) =>
    apiClient.post(`/training/admin/enrollments/${id}/reactivate/`),

  // Certifications
  getCertifications: (params?: Record<string, string>) =>
    apiClient.get('/training/admin/certifications/', { params }),

  revokeCertification: (id: string, reason: string) =>
    apiClient.post(`/training/admin/certifications/${id}/revoke/`, { reason }),

  // Certification Templates (diploma/badge HTML)
  getCertificationTemplates: async (
    params?: Record<string, string>,
  ): Promise<CertificationTemplateAdmin[]> => {
    // DRF default pagination wraps the list in { results, count } — we normalize
    // to a bare array for callers.
    const res = await apiClient.get<
      CertificationTemplateAdmin[] | { results: CertificationTemplateAdmin[]; count: number }
    >('/training/admin/certification-templates/', { params })
    return Array.isArray(res) ? res : res.results
  },

  getCertificationTemplate: (id: string) =>
    apiClient.get<CertificationTemplateAdmin>(`/training/admin/certification-templates/${id}/`),

  createCertificationTemplate: (
    data: Partial<CertificationTemplateAdmin>,
    scope?: { company?: string; project?: string },
  ) =>
    apiClient.post<CertificationTemplateAdmin>(
      '/training/admin/certification-templates/',
      data,
      { params: (scope as Record<string, string>) || undefined },
    ),

  updateCertificationTemplate: (id: string, data: Partial<CertificationTemplateAdmin>) =>
    apiClient.patch<CertificationTemplateAdmin>(
      `/training/admin/certification-templates/${id}/`,
      data,
    ),

  deleteCertificationTemplate: (id: string) =>
    apiClient.delete(`/training/admin/certification-templates/${id}/`),

  previewCertificationTemplate: (id: string) =>
    apiClient.post<{
      diploma_html: string
      badge_html: string | null
      title: string
      subtitle: string
    }>(`/training/admin/certification-templates/${id}/preview/`),
}

// ─── Admin types (CertificationTemplate) ───

export interface CertificationTemplateAdmin {
  id: string
  project: string | null
  project_name: string | null
  title: string
  subtitle: string
  diploma_html_template: string
  badge_html_template: string
  logo_url: string
  signature_image_url: string
  signer_name: string
  signer_title: string
  social_share_text: string
  social_share_hashtags: string
  is_active: boolean
  created_at: string
  updated_at: string
}
